/**
 * Pain, 0-10. FR-205.
 *
 * A range input rather than eleven buttons. Eleven targets across a 320px screen
 * would be about 28px each, below the 44px minimum in NFR-10 — and a slider is
 * keyboard-operable for free. The trade is that a slider needs a spoken value,
 * which `aria-valuetext` supplies in words rather than as a bare number.
 *
 * The scale is integers only. Data model Section 1: fractional pain is false
 * precision, because nobody can tell 6.4 from 6.7 about their own body.
 *
 * ## Why there is a separate "None" button
 *
 * A slider whose resting position is 0 cannot record 0: the value never changes,
 * so no event fires, and "no pain today" becomes the one answer the control
 * cannot express. That is a real gap — a recorded zero and an unrecorded day are
 * different facts, the same distinction FR-004 draws for whole days, and
 * "nothing hurt today" is worth being able to say.
 *
 * So 0 gets its own control. The slider covers 1-10, where dragging always
 * produces a change.
 */
import { t } from '@copy/index'

export interface PainScaleProps {
  value: number | undefined
  onChange: (value: number | undefined) => void
}

export function PainScale({ value, onChange }: PainScaleProps) {
  const recorded = value !== undefined
  const sliderValue = recorded ? value : 0

  return (
    <div>
      <div className="flex items-baseline justify-between pb-1">
        <label htmlFor="pain-scale" className="text-sm font-medium">
          {t('entry.pain')}
        </label>
        <span className="text-sm text-ink-muted" aria-hidden="true">
          {recorded
            ? value === 0
              ? t('entry.painNone')
              : String(value)
            : t('entry.notRecorded')}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <input
          id="pain-scale"
          type="range"
          min={0}
          max={10}
          step={1}
          value={sliderValue}
          aria-valuetext={
            recorded ? t('entry.painValue', { value }) : t('entry.notRecorded')
          }
          onChange={(event) => onChange(Number(event.target.value))}
          onPointerDown={(event) => {
            if (value === undefined) {
              const rect = event.currentTarget.getBoundingClientRect()
              const x = event.clientX - rect.left
              const percent = x / rect.width
              if (percent < 0.15) {
                onChange(0)
              }
            }
          }}
          className={`h-11 flex-1 accent-accent ${recorded ? '' : 'opacity-50'}`}
        />

        {recorded ? (
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className="min-h-11 rounded-lg border border-line px-3 text-xs transition-all duration-200 hover:bg-line/40 hover:text-ink active:scale-95"
          >
            {t('entry.clearField')}
          </button>
        ) : null}
      </div>
    </div>
  )
}
